import { useEffect, useMemo, useState } from "react";
import styled from "@emotion/styled";
import { isControl } from "@jsonforms/core";
import { JsonForms } from "@jsonforms/react";
import { vanillaCells, vanillaRenderers } from "@jsonforms/vanilla-renderers";
import { Flex, Text } from "styles/utility-components";

import AccountSearchControl from "components/form_components/json_forms/account_search/AccountSearchControl";
import AccountSearchTester from "components/form_components/json_forms/account_search/AccountSearchTester";
import DateControl from "components/form_components/json_forms/date/DateControl";
import DateControlTester from "components/form_components/json_forms/date/DateControlTester";
import EnumControl from "components/form_components/json_forms/enum/EnumControl";
import EnumControlTester from "components/form_components/json_forms/enum/EnumControlTester";
import NumberControl from "components/form_components/json_forms/number/NumberControl";
import NumberTester from "components/form_components/json_forms/number/NumberTester";
import OwnerSearchControl from "components/form_components/json_forms/owner_search/OwnerSearchControl";
import OwnerSearchTester from "components/form_components/json_forms/owner_search/OwnerSearchTester";
import {
  FormError,
  getRequiredFieldErrors,
} from "components/form_components/json_forms/requiredFieldErrors";
import StringControl from "components/form_components/json_forms/string/StringControl";
import StringControlTester from "components/form_components/json_forms/string/StringControlTester";
import TextAreaControl from "components/form_components/json_forms/textarea/TextAreaControl";
import TextAreaTester from "components/form_components/json_forms/textarea/TextAreaTester";
import { ParagonCrmProvider } from "components/integrations/managed_integrations/crmProviders";
import Button from "components/ui/button/Button";
import Modal from "components/ui/modal/Modal";
import SkeletonText from "components/ui/skeleton/SkeletonText";
import { appRoutes } from "lib/appRoutes";
import { MappableField } from "lib/generated/integration-api";
import { useCrmSendPerson } from "lib/hooks/api/integrations/useCrmSendPerson";
import { InvalidCrmField, refusedFieldsFrom } from "lib/integrations/crmSendErrors";
import { CrmRecordForm } from "lib/integrations/paragonCrm/buildRecordForm";
import { fieldMappingSectionId } from "lib/integrations/paragonCrm/fieldCompatibility";
import {
  CrmContactFields,
  CrmPerson,
  CrmRecordKind,
  CrmRecordSeed,
  PERSON_FIELD_KEYS,
  personSeed,
  RECORD_LABELS,
} from "lib/integrations/paragonCrm/recordFields";
import { CrmPushRequest } from "lib/integrations/paragonCrm/types";
import { EventNames, TrackingProvider, useTracking } from "lib/tracking";
import { getFormattedDate } from "lib/utils";
import MappingCombobox from "./field_mapping/MappingCombobox";

const renderers = [
  ...vanillaRenderers,
  { tester: StringControlTester, renderer: StringControl },
  { tester: TextAreaTester, renderer: TextAreaControl },
  { tester: NumberTester, renderer: NumberControl },
  { tester: DateControlTester, renderer: DateControl },
  // Ranks above the string control, so a field whose options arrived becomes a dropdown while one
  // without them stays a text box.
  { tester: EnumControlTester, renderer: EnumControl },
  { tester: AccountSearchTester, renderer: AccountSearchControl },
  { tester: OwnerSearchTester, renderer: OwnerSearchControl },
];

/**
 * The bid data a mapping can reach that the opportunity payload has no room for. Optional
 * throughout: a lead carries none of it, and a bid is rarely filled in completely.
 */
export type CrmBidFields = {
  description?: string;
  clarificationDeadline?: string;
  submissionDate?: string;
  startDate?: string;
  awardDate?: string;
};

export type { CrmContactFields, CrmPerson };

/** A Stotles record on its way to the CRM. */
export type CrmRecord = CrmBidFields &
  CrmContactFields & {
    kind: CrmRecordKind;
    guid: string;
    /** Names the record in the toast, and fills the form's own name field where it has one. */
    name: string;
    /** Absent for a record with no page of its own to link back to, as a contact has none. */
    stotlesRoute?: string;
    value?: number;
    expiryDate?: string;
  };

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The CRM the company has connected, which names itself and its objects in the copy below. */
  provider: ParagonCrmProvider;
  record: CrmRecord;
  /**
   * The people this record could be sent as, which is the contacts saved to it. Only consulted
   * when the mapping asks for a person's details; a record whose mapping does not is sent without
   * anyone being chosen.
   */
  people?: CrmPerson[];
  /**
   * Pushes what the form produced. The caller owns the request, because which route a record takes
   * is a property of the record and not of the form: a contact carries ids a lead does not have.
   *
   * Awaited, and a rejection keeps the modal open: a refusal names fields the user has to change,
   * and closing first would throw away everything they typed.
   *
   * `person` is passed only for a CRM whose target object holds no person of its own, and carries
   * the chosen contact translated through the company's contact mapping.
   */
  onSend: (request: CrmPushRequest, person?: CrmPushRequest) => Promise<unknown>;
};

/** The date controls take a plain day, and a CRM reads one back. */
function asDay(date: string | undefined): string | undefined {
  return date ? getFormattedDate(new Date(date)) : undefined;
}

/**
 * What the form starts filled in with.
 *
 * A record with a page of its own seeds the description with a link back to it, which is the reason
 * a lead's description is worth sending at all — so a record that has a description too keeps both.
 */
function recordSeed(record: CrmRecord): CrmRecordSeed {
  const link = record.stotlesRoute
    ? `${RECORD_LABELS[record.kind]} URL: ${window.location.origin}${record.stotlesRoute}`
    : "";
  const description = [record.description, link].filter(Boolean).join("\n\n");

  return {
    title: record.name,
    description,
    value: record.value,
    expiryDate: asDay(record.expiryDate),
    clarificationDeadline: asDay(record.clarificationDeadline),
    submissionDate: asDay(record.submissionDate),
    startDate: asDay(record.startDate),
    awardDate: asDay(record.awardDate),
    ...personSeed(record),
  };
}

/**
 * Who the record is being sent as. One contact is stated rather than offered; several are offered
 * with nothing chosen, because which person a salesperson works is theirs to decide.
 */
function PersonChoice({
  people,
  personId,
  onChange,
  objectNoun,
  providerName,
  dataType,
  required,
}: {
  people: CrmPerson[];
  personId: string | undefined;
  onChange: (id: string | undefined) => void;
  objectNoun: string;
  providerName: string;
  dataType: string;
  /** The object is refused without a contact, rather than merely having fields describing one. */
  required: boolean;
}): JSX.Element {
  // A HubSpot lead is not a person, it is created from one — so it cannot borrow the wording that
  // explains why the other CRMs ask.
  const because = required
    ? `${objectNoun} in ${providerName} is created from a contact`
    : `${objectNoun} in ${providerName} is a person`;

  // Nothing can be sent, as opposed to being sent with the person's fields empty, so this says what
  // to do rather than what will happen.
  if (people.length === 0 && required) {
    return (
      <Text description>
        {because}, and this {dataType} has none saved to it. Add a contact before sending it.
      </Text>
    );
  }

  if (people.length === 0) {
    return (
      <Text description>
        {because}, and this {dataType} has no contacts saved to it — so the fields describing one
        will be sent empty. Add a contact to fill them.
      </Text>
    );
  }

  if (people.length === 1) {
    return (
      <Text description>
        {because}, so this {dataType} is being sent as {people[0].name}.
      </Text>
    );
  }

  return (
    <Flex column gap={8}>
      <Text description>
        {because}, so choose which of this {dataType}&apos;s contacts it describes.
      </Text>
      <MappingCombobox
        options={people.map((candidate) => ({
          value: candidate.id,
          // Two people at one buyer are told apart by their job, so a name on its own can name
          // either of them.
          label: candidate.jobTitle ? `${candidate.name} — ${candidate.jobTitle}` : candidate.name,
        }))}
        value={personId}
        onChange={onChange}
        label="Contact to send as"
        placeholder="Choose a contact"
        searchPlaceholder="Search contacts"
        emptyMessage="No matching contacts"
      />
    </Flex>
  );
}

/**
 * Sends a record to the CRM the company connected through Paragon. The form is generated from
 * their field mapping, so which fields appear is controlled on the mapping page rather than here.
 */
export function SendToParagonCrmModal({
  open,
  onOpenChange,
  provider,
  record,
  people = [],
  onSend,
}: Props): JSX.Element {
  const { logEvent } = useTracking();
  const dataType = RECORD_LABELS[record.kind];

  // Callers pass a memoised record, so this holds still between renders. It only has to: the form
  // is seeded once per opening, and re-deriving it is harmless beyond the work.
  const recordFields = useMemo(() => recordSeed(record), [record]);

  /**
   * The CRM fields the provider has refused this record for want of. Separate from `refusedFields`,
   * which is cleared on each submit: clearing mid-submit would take away a control being filled in.
   */
  const [demandedCrmFields, setDemandedCrmFields] = useState<string[]>([]);

  const {
    form,
    crmFields,
    isLoading,
    isConfigured,
    error,
    person,
    choose,
    personRequest,
    needsAPerson,
    objectRequiresAPerson,
    needsAContactMapping,
    isBlocked,
    forget,
  } = useCrmSendPerson({
    provider,
    recordKind: record.kind,
    recordSeed: recordFields,
    people,
    demandedCrmFields,
  });

  const [data, setData] = useState<Record<string, unknown>>({});
  const [isInitialised, setIsInitialised] = useState(false);
  const [isSending, setIsSending] = useState(false);
  /** The fields the last push was refused for, as the service named them. */
  const [refusedFields, setRefusedFields] = useState<InvalidCrmField[]>([]);

  // Seeded once per opening rather than once per mount: this component stays mounted while closed,
  // and the mapping can be changed on its own page in between. Keyed on `open` rather than on
  // `form` alone because `form` takes a new identity whenever the mapping query settles again, so
  // a refetch would otherwise discard whatever the user had typed.
  useEffect(() => {
    if (!open) {
      setIsInitialised(false);
      setRefusedFields([]);
      // Reset to the same array when there is nothing to reset. A fresh one would rebuild the
      // form, which this effect depends on, which would run it again — without end.
      setDemandedCrmFields((known) => (known.length === 0 ? known : []));
      // Who it is being sent as is asked again rather than carried over: choosing on a reopening's
      // behalf is the decision this form exists not to make.
      forget();
      return;
    }
    if (form && !isInitialised) {
      setData(form.initialData);
      setIsInitialised(true);
    }
  }, [open, form, isInitialised, forget]);

  // Refills only the person's fields on a new choice: a CRM field typed by hand belongs to the
  // record, not to whoever it is being sent as.
  useEffect(() => {
    if (!isInitialised || !form) return;
    setData((current) => {
      const refilled = { ...current };
      for (const key of PERSON_FIELD_KEYS) {
        // Cleared where the new person has nothing for it: a key the seed omits would otherwise
        // keep the last person's value and send one contact's email on another's record.
        if (key in form.initialData) refilled[key] = form.initialData[key];
        else delete refilled[key];
      }
      return refilled;
    });
    // Keyed on the person alone: `form` changes identity whenever its queries settle, and reacting
    // to that would overwrite what the user typed into a person's field.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [person?.id]);

  /**
   * Closed on success only. A refusal names fields the user has to change, and closing first would
   * throw away everything they typed — leaving a toast telling them to fix a form that is gone.
   */
  const submit = async () => {
    if (!form) return;

    setRefusedFields([]);
    setIsSending(true);
    try {
      // The entity goes with the push, read from the mapping this form was built from — so a
      // mapping changed on its own page takes effect on the next push with nothing to keep in step.
      await onSend(form.toPushRequest(data), personRequest);
    } catch (error) {
      // The caller has already explained it in a toast; this names the fields on the form itself,
      // which is what says where to look.
      const refused = await refusedFieldsFrom(error);
      setRefusedFields(refused);
      setDemandedCrmFields((known) => demandedAfter(known, refused));
      return;
    } finally {
      setIsSending(false);
    }

    logEvent(EventNames.objectSentToCRM, {
      "Data types": dataType,
      "Connected provider": provider.providerType,
    });
    onOpenChange(false);
  };

  // Told apart because they send the user to different places: a value is fixed in this form, a
  // mapping on its own page, and a field the CRM never received is filled in below once the form
  // has grown a control for it. A refusal that named none of the three is treated as a mapping
  // fault, which is what the ones we can recognise mostly are.
  const valueFaults = refusedFields.filter((field) => field.fault === "value" && field.reason);
  // Answerable here only once the field is on the form. One the object's schema does not describe,
  // or describes as a reference — which has no control to offer — is left to the mapping notice,
  // since pointing a mapped field at it is then the only way through.
  const collectableFaults = refusedFields.filter(
    (field) => field.fault === "missing" && !!form && !!formKeyFor(form, field.name),
  );
  const mappingFaults = refusedFields.filter(
    (field) => !valueFaults.includes(field) && !collectableFaults.includes(field),
  );

  // A value fault goes onto its own control. What is left over — a mapping fault, or a value fault
  // naming a field this form does not show — has nowhere to sit but the notice above the form.
  //
  // Memoised because JSON-Forms rebuilds its validation core whenever this prop changes identity,
  // which a fresh array every render would provoke on every keystroke.
  const fieldErrors = useMemo(
    () => (form ? toFieldErrors(form, valueFaults) : []),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- valueFaults is derived from these two
    [form, refusedFields],
  );

  // The empty fields the schema marks required, which JSON-Forms no longer works out for itself:
  // it is running with validation off, because ajv compiles a schema with `new Function` and the
  // Content-Security-Policy blocks that wherever it is enforced.
  const requiredErrors = useMemo(
    () => (form ? getRequiredFieldErrors(form.schema, data) : []),
    [form, data],
  );
  /** Both kinds of fault reach the controls through the one prop JSON-Forms offers for them. */
  const additionalErrors = useMemo<FormError[]>(
    () => [...fieldErrors, ...requiredErrors],
    [fieldErrors, requiredErrors],
  );
  // Only an unfilled required field holds Send. A value the customer is being asked to correct must
  // never disable the button they have to press to correct it, so a CRM refusal does not count.
  const hasErrors = requiredErrors.length > 0;
  const noticeFields = [
    ...mappingFaults,
    ...valueFaults.filter((fault) => !form || !formKeyFor(form, fault.name)),
  ];

  /** A dead end the user can only leave by mapping something, with the way out of it. */
  const configureFields = (message: string, section: CrmRecordKind) => (
    <Flex column gap={16}>
      <Text>{message}</Text>
      <Flex justifyContent="flex-end">
        <Button
          as="a"
          {...appRoutes.integrationFieldMapping}
          hash={fieldMappingSectionId(section)}
          onClick={() => onOpenChange(false)}
        >
          Configure fields
        </Button>
      </Flex>
    </Flex>
  );

  // Early returns rather than a ternary chain: each arm is a sizeable block, and nesting them
  // reads poorly. See the "no nested ternaries" rule in apps/webapp/AGENTS.md.
  const renderBody = () => {
    if (isLoading) {
      return (
        <>
          <SkeletonText width="100%" />
          <SkeletonText width="100%" />
          <SkeletonText width="100%" />
        </>
      );
    }

    // Told apart from an absent mapping: sending someone to configure one they already have is the
    // wrong instruction, and the page would show it to them.
    if (error) {
      return (
        <Text color="sysDestructiveDefault">
          Could not read your {provider.name} field mapping. Please try again.
        </Text>
      );
    }

    if (!isConfigured || !form) {
      return configureFields(
        `No field mapping is set up for ${provider.name} yet, so there is nothing to send. Choose where each field goes in your integration settings, then try again.`,
        record.kind,
      );
    }

    if (needsAContactMapping) {
      return configureFields(
        `${provider.objectNoun(form.object.logicalName)} in ${provider.name} is created from a contact, and no contact mapping is set up yet. Choose where a contact's fields go in your integration settings, then try again.`,
        "contact",
      );
    }

    return (
      <>
        <Text description>
          Creating {provider.objectNoun(form.object.logicalName)} in {provider.name}. These fields
          come from your field mapping.
        </Text>
        {collectableFaults.length > 0 && (
          <Text color="sysDestructiveDefault" data-testid="crm-demanded-fields">
            {provider.name} will not create {provider.objectNoun(form.object.logicalName)} without{" "}
            {collectableFaults.map((field) => labelFor(form, crmFields, field.name)).join(", ")}.
            Fill {collectableFaults.length === 1 ? "it" : "them"} in below and send again.
          </Text>
        )}
        {noticeFields.length > 0 && (
          <Text color="sysDestructiveDefault" data-testid="crm-refused-fields">
            {provider.name} would not accept{" "}
            {noticeFields.map((field) => labelFor(form, crmFields, field.name)).join(", ")}. Check
            where {noticeFields.length === 1 ? "that field is" : "those fields are"} mapped, then
            try again.
          </Text>
        )}
        {needsAPerson && (
          <PersonChoice
            people={people}
            personId={person?.id}
            onChange={choose}
            objectNoun={provider.objectNoun(form.object.logicalName)}
            providerName={provider.name}
            dataType={dataType.toLowerCase()}
            required={objectRequiresAPerson}
          />
        )}
        <JsonForms
          schema={form.schema}
          uischema={form.uischema}
          data={data}
          renderers={renderers}
          cells={vanillaCells}
          // ajv compiles every schema with `new Function`, which the Content-Security-Policy
          // blocks: with validation on, the modal dies with an EvalError before it renders.
          // Nothing reaches ajv with it off, and `additionalErrors` carries what it used to find.
          validationMode="NoValidation"
          additionalErrors={additionalErrors}
          onChange={({ data: updated }) => setData(updated)}
        />
        <ButtonRow>
          <Button
            as="a"
            buttonType="secondary"
            {...appRoutes.integrationFieldMapping}
            hash={fieldMappingSectionId(record.kind)}
            onClick={() => onOpenChange(false)}
          >
            Edit field mapping
          </Button>
          {/* Names the record rather than the CRM: the heading above already says where it is
              going, and repeating that here would leave the modal with two identical controls. */}
          {/* Nobody chosen is not a form error — the controls are simply empty — so the button is
              held here rather than by the schema, which would have to be rebuilt per person. */}
          <Button disabled={hasErrors || isSending || isBlocked} onClick={submit}>
            Send {dataType.toLowerCase()}
          </Button>
        </ButtonRow>
      </>
    );
  };

  return (
    <TrackingProvider
      data={{ "Context source": "Send to CRM modal", "Connected provider": provider.providerType }}
    >
      <Modal open={open} onOpenChange={onOpenChange}>
        <Flex column gap={16}>
          <Text h2>Send to {provider.name}</Text>
          {renderBody()}
        </Flex>
      </Modal>
    </TrackingProvider>
  );
}

/** The form key a CRM field name is mapped to, if this form carries it at all. */
function formKeyFor(form: CrmRecordForm, crmField: string): string | undefined {
  return Object.keys(form.crmFieldByKey).find((key) => form.crmFieldByKey[key] === crmField);
}

/**
 * What a CRM field is called: the form's own label where it shows the field, the CRM's label where
 * it does not, and the internal name only for a field the schema does not describe.
 */
function labelFor(form: CrmRecordForm, crmFields: MappableField[], crmField: string): string {
  const key = formKeyFor(form, crmField);
  const control = form.uischema.elements.find(
    (element) => isControl(element) && element.scope === `#/properties/${key}`,
  );
  if (control && isControl(control) && typeof control.label === "string") return control.label;
  return crmFields.find((field) => field.id === crmField)?.name ?? crmField;
}

/**
 * The CRM fields to collect once a refusal has been read, accumulated rather than replaced: a CRM
 * stops at the first rule it fails, so each refusal can name a different field and the ones already
 * collected have to stay on the form.
 *
 * Only a missing field is collectable. A value the CRM rejected already has a control, which its
 * own error is attached to.
 */
export function demandedAfter(known: string[], refused: InvalidCrmField[]): string[] {
  return [
    ...new Set([
      ...known,
      ...refused.filter((field) => field.fault === "missing").map((field) => field.name),
    ]),
  ];
}

/**
 * A value the CRM refused, shaped as the validation error JSON-Forms merges into what each control
 * sees — which is what puts the message and the red border on the field itself rather than in a
 * notice above the form. Only fields this form actually shows can carry one.
 */
function toFieldErrors(form: CrmRecordForm, faults: InvalidCrmField[]): FormError[] {
  return faults.flatMap((fault) => {
    const key = formKeyFor(form, fault.name);
    if (!key) return [];
    return [
      {
        instancePath: `/${key}`,
        message: fault.reason,
        keyword: "crm",
        schemaPath: "",
        params: {},
      },
    ];
  });
}

const ButtonRow = styled.div({
  display: "flex",
  justifyContent: "space-between",
  gap: 8,
});
